const REDACTION_PLACEHOLDER = '[redacted]';
const AUTH_CALLBACK_PATHS = new Set(['/auth/callback', '/auth/callback/']);

// Anonymous-share pages carry a bearer TOKEN as a PATH SEGMENT (not a query param):
// `/ad/<token>` and `/share/<token>`. Sentry/Datadog transaction and view names are
// frequently the bare relative path, so the token must be masked to a stable
// placeholder wherever it appears — in a full URL value and in free text.
const TOKEN_PATH_URL_PATTERN = /^\/(ad|share)\/[^/]+/;
const TOKEN_PATH_TEXT_PATTERN = /\/(ad|share)\/[\w=-]+/g;

/** Mask the token segment of an anonymous-share path (`/ad/<token>` → `/ad/:token`),
 *  dropping any query/hash tail that could echo the token. Returns null when the
 *  path is not a share path. Extracted to keep `redactSensitiveUrl` under its
 *  cognitive-complexity budget. */
function maskTokenPath(pathname: string, origin: string, isRelative: boolean): string | null {
  const match = TOKEN_PATH_URL_PATTERN.exec(pathname);
  if (!match) {
    return null;
  }
  const maskedPath = pathname.replace(TOKEN_PATH_URL_PATTERN, `/${match[1]}/:token`);
  return isRelative ? maskedPath : `${origin}${maskedPath}`;
}

/** Query-param names whose VALUES are scrubbed from every telemetry surface,
 *  matched case-insensitively both here and in `SENSITIVE_PARAM_VALUE_PATTERN`.
 *  An ALLOW-LIST of keys to redact, never a deny-list of keys to keep: a param
 *  absent from this set ships to Sentry and Datadog RUM verbatim, so any key that
 *  can carry user-typed free text or credentials belongs here. Opaque ids, enums
 *  and dates stay intact - that is what leaves a redacted URL diagnosable. */
const SENSITIVE_URL_PARAMS: ReadonlySet<string> = new Set([
  'search',
  'q',
  'repRecipient',
  'fundingEntity',
  'fromEmail',
  'toEmail',
  'subjectSearch',
  // PostgREST column filters carrying typed text: title searches (`title=ilike.%…%`) are often
  // a constituent's name, and the column name is the only key the value rides under.
  'title',
  // PostgREST logic-tree params: `.or(...)`/`.and(...)` filters ride the GET
  // url verbatim, and hooks embed ilike search terms (constituent names,
  // emails) inside them - an unredacted `or=` in a Sentry breadcrumb defeats
  // every code-only error throw upstream.
  'or',
  'and',
  'code',
  'error',
  'error_code',
  'error_description',
  'access_token',
  'refresh_token',
  'provider_token',
  'provider_refresh_token',
  'id_token',
  'token',
  'token_hash',
]);
const SENSITIVE_OBJECT_KEYS: ReadonlySet<string> = new Set([
  'accountlabel',
  'label',
  'channel',
  'workspace',
  'accesstoken',
  'refreshtoken',
  'providertoken',
  'providerrefreshtoken',
  'idtoken',
  'code',
  'error',
  'errorcode',
  'errordescription',
  'token',
  'tokenhash',
  // External reply hand-over: the approved text and the hashes bound to it.
  'rendition',
  'bodyhtml',
  'contenthash',
  'approvedcontenthash',
  'expectedhash',
  'pexpectedhash',
  // "Sent with changes": the text staff recorded as actually sent.
  'senttext',
]);

/** Lower-cased mirror for the structured walker, which matches param names it is
 *  handed rather than ones it supplies. */
const SENSITIVE_URL_PARAM_KEYS: ReadonlySet<string> = new Set(
  [...SENSITIVE_URL_PARAMS].map((key) => key.toLowerCase()),
);

/** Escapes regex metacharacters so a key can only ever match literally, never be
 *  interpreted as pattern structure. */
function escapeRegExp(value: string): string {
  return value.replaceAll(/[$()*+.?[\\\]^{|}]/g, String.raw`\$&`);
}

export const __escapeRegExpForTests = escapeRegExp;

/** Bare `key=value` scrubber for telemetry text that never parses as a URL -
 *  transaction names, console-captured breadcrumbs, exception prose. Derived from
 *  `SENSITIVE_URL_PARAMS` (longest key first, so a key that prefixes another
 *  cannot shadow it) so no key can be redacted on the structured surface and leak
 *  on this one. Bounded and non-nested: matching must stay linear (ReDoS). */
// eslint-disable-next-line security/detect-non-literal-regexp -- alternation is built from escaped module-private literals fixed at build time, never from runtime input
const SENSITIVE_PARAM_VALUE_PATTERN = new RegExp(
  String.raw`(^|[\s#&?])(${[...SENSITIVE_URL_PARAMS]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join('|')})=([^\s"#&';<>?]{1,4096})`,
  'gi',
);
const ABSOLUTE_URL_PATTERN = /https?:\/\/[^\s"';<>]{1,4096}/g;
const AUTH_CALLBACK_URL_PATTERN = /\/auth\/callback[^\s"',;<>]{0,4096}/g;

/** A schemeless `/path?query` in telemetry text matches no absolute-URL pattern,
 *  and the bare `key=value` scrubber below compares LITERAL key names while the
 *  structured parser percent-decodes them (`%71=` is `q=`), so text-only handling
 *  leaves the two surfaces disagreeing about what a sensitive key is. Routing the
 *  substring through `redactSensitiveUrl` keeps both on one decoder.
 *  The lookbehind excludes the characters that can only appear mid-token, so the
 *  path of an absolute URL (already rewritten by `ABSOLUTE_URL_PATTERN`) is never
 *  re-matched and re-encoded, and `(?!\/)` leaves protocol-relative `//host/...`
 *  to that pattern instead of stripping its host. Bounded and non-nested, and the
 *  path class excludes `?` so the split point is unambiguous: matching stays
 *  linear (ReDoS). */
const RELATIVE_URL_TEXT_PATTERN =
  /(?<![\w%+./:@-])\/(?!\/)[^\s"',;<>?]{0,2048}\?[^\s"';<>]{1,4096}/g;
const MAX_REDACTION_DEPTH = 8;

/** `q[]=` and `q[0]=` are the same parameter as `q=` to every array-style query
 *  encoder, so a TRAILING bracket group is dropped before the allow-list
 *  comparison. Only a trailing one: a key that merely contains brackets
 *  (`filters[status]`) reduces to `filters`, which is absent from the list, so it
 *  still ships verbatim. Index scanning rather than a regex: a trailing-anchored
 *  `[^\]]*` is super-linear on adversarial input (ReDoS). */
function stripArrayParamSuffix(key: string): string {
  if (!key.endsWith(']')) {
    return key;
  }
  const suffixStart = key.lastIndexOf('[');
  return suffixStart > 0 ? key.slice(0, suffixStart) : key;
}

function normalizeObjectKey(key: string): string {
  return key.toLowerCase().replaceAll('_', '').replaceAll('-', '');
}

function redactParams(params: URLSearchParams): boolean {
  let mutated = false;
  // Snapshot the names first: `set` collapses duplicates and would disturb a live iterator.
  for (const key of new Set(params.keys())) {
    if (SENSITIVE_URL_PARAM_KEYS.has(stripArrayParamSuffix(key.toLowerCase()))) {
      params.set(key, REDACTION_PLACEHOLDER);
      mutated = true;
    }
  }
  return mutated;
}

function redactHash(parsed: URL): boolean {
  if (!parsed.hash) {
    return false;
  }

  const rawHash = parsed.hash.slice(1);
  const queryIndex = rawHash.indexOf('?');
  if (queryIndex !== -1) {
    const prefix = rawHash.slice(0, queryIndex + 1);
    const params = new URLSearchParams(rawHash.slice(queryIndex + 1));
    if (redactParams(params)) {
      parsed.hash = `${prefix}${params.toString()}`;
      return true;
    }
    return false;
  }

  const hadQueryPrefix = rawHash.startsWith('?');
  const params = new URLSearchParams(hadQueryPrefix ? rawHash.slice(1) : rawHash);
  if (!redactParams(params)) {
    return false;
  }
  parsed.hash = `${hadQueryPrefix ? '?' : ''}${params.toString()}`;
  return true;
}

/**
 * Redacts constituent and authentication material from absolute or relative URLs.
 * OAuth callbacks are reduced to their path because every query and fragment value
 * on that route belongs to the authentication exchange.
 */
export function redactSensitiveUrl(value: unknown): unknown {
  if (typeof value !== 'string' || value.length === 0) {
    return value;
  }

  let parsed: URL;
  let isRelative = false;
  try {
    parsed = new URL(value);
  } catch {
    try {
      parsed = new URL(value, 'https://_redact-base/');
      isRelative = true;
    } catch {
      return value;
    }
  }

  if (AUTH_CALLBACK_PATHS.has(parsed.pathname)) {
    parsed.search = '';
    parsed.hash = '';
    return isRelative ? parsed.pathname : parsed.toString();
  }

  const maskedTokenUrl = maskTokenPath(parsed.pathname, parsed.origin, isRelative);
  if (maskedTokenUrl !== null) {
    return maskedTokenUrl;
  }

  const searchMutated = redactParams(parsed.searchParams);
  const hashMutated = redactHash(parsed);
  if (!searchMutated && !hashMutated) {
    return value;
  }
  if (isRelative) {
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }
  return parsed.toString();
}

function redactUrlCandidate(candidate: string): string {
  const redacted = redactSensitiveUrl(candidate);
  return typeof redacted === 'string' ? redacted : candidate;
}

/** Scrubs URL-shaped and bare key/value authentication material from telemetry text. */
export function redactSensitiveText(text: string): string {
  let redacted = text.replaceAll(ABSOLUTE_URL_PATTERN, redactUrlCandidate);
  redacted = redacted.replaceAll(AUTH_CALLBACK_URL_PATTERN, redactUrlCandidate);
  redacted = redacted.replaceAll(RELATIVE_URL_TEXT_PATTERN, redactUrlCandidate);
  // Mask the token segment of a bare relative share path (e.g. a transaction name
  // "/ad/abc123" → "/ad/:token"); absolute-URL variants were handled just above.
  redacted = redacted.replaceAll(
    TOKEN_PATH_TEXT_PATTERN,
    (_match, segment: string) => `/${segment}/:token`,
  );
  return redacted.replaceAll(
    SENSITIVE_PARAM_VALUE_PATTERN,
    (_match, boundary: string, key: string) => `${boundary}${key}=${REDACTION_PLACEHOLDER}`,
  );
}

/** Redacts every reachable string leaf of a telemetry payload with bounded
 * depth, and returns the redacted value. Mutable containers are scrubbed in
 * place; a frozen container is scrubbed on a shallow clone the caller swaps in
 * (fail-closed — a frozen sensitive value is redacted, never forwarded raw).
 * The one thing never touched is an accessor property: reading it would invoke
 * a getter that may run live-library code (Sentry SDK internals reachable from
 * an event), so accessors are skipped without being read — this is a load-bearing
 * guard, not dead defensiveness. Accessors do appear on SDK-owned objects; they
 * do NOT appear in the normalized/shipping payload fields (each caller's SDK
 * materializes those into plain data before the hook, and the live-object root
 * `sdkProcessingMetadata` is detached from the walk — see `redactOwnDataProperty`
 * for the exact contract), so skipping them is fail-safe, not a coverage gap. */
export function redactSensitiveStringsDeep(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') {
    return redactSensitiveText(value);
  }
  if (depth >= MAX_REDACTION_DEPTH || value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    const target = toWritableClone(value as unknown[]);
    const record = target as unknown as Record<string, unknown>;
    for (let index = 0; index < target.length; index += 1) {
      redactOwnDataProperty(record, String(index), (child) =>
        redactSensitiveStringsDeep(child, depth + 1),
      );
    }
    return target;
  }
  const target = toWritableClone(value as Record<string, unknown>);
  for (const key of Object.keys(target)) {
    redactRecordKey(target, key, depth);
  }
  return target;
}

/** A frozen container can't be redacted in place, so return a plain shallow copy
 * the caller's writable parent slot swaps in. The copy carries own DATA
 * properties only: accessor properties are omitted, never read (their getter
 * could run live-library code or throw — the crash class this whole module
 * guards against), so a frozen accessor value is dropped fail-closed rather than
 * shipped raw. Non-frozen values are returned unchanged for in-place redaction.
 *
 * Constrained to arrays and plain records — the only shapes the walkers hand it.
 * The clone doesn't preserve prototypes, so widening this to `object` would be
 * unsound (a frozen `Date`/`Map` would return as `{}`). */
export function toWritableClone<T extends unknown[] | Record<string, unknown>>(value: T): T {
  if (!Object.isFrozen(value)) {
    return value;
  }
  if (Array.isArray(value)) {
    // Sparse (holes, not defined `undefined`s) so omitted accessor/hole indices
    // stay absent rather than becoming own `undefined` data properties that
    // change the serialized shape.
    const copy: unknown[] = [];
    copy.length = value.length;
    for (let i = 0; i < value.length; i += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, i);
      if (descriptor && descriptor.get === undefined && descriptor.set === undefined) {
        copy[i] = descriptor.value;
      }
    }
    return copy as unknown as T;
  }
  const copy: Record<string, unknown> = {};
  for (const key of Object.keys(value)) {
    const descriptor = Object.getOwnPropertyDescriptor(value, key);
    if (descriptor && descriptor.get === undefined && descriptor.set === undefined) {
      copy[key] = descriptor.value;
    }
  }
  return copy as T;
}

/** Applies `redact` to one own property of `record` and writes the result back
 * fail-closed. A plain writable slot is reassigned; a non-writable but
 * *configurable* data slot is redefined (so a read-only sensitive value is
 * still scrubbed, not forwarded); an accessor property is skipped WITHOUT being
 * read — reading invokes its getter, which on a live library object (Sentry SDK
 * internals reachable from an event) runs arbitrary code, and assigning a
 * getter-only accessor throws in strict mode (the crash this guards against).
 * Residual: an enumerable accessor, or a non-configurable non-writable data
 * slot, on a non-frozen object is left as-is — the redacted value can't be
 * written back and the property can't be removed without risk. `JSON.stringify`
 * would still serialize such a slot, so this is the one narrow path where a
 * value could ship unredacted. It does not occur for real payload: each caller's
 * SDK materializes event fields into plain writable data before the hook runs —
 * Sentry `normalizeEvent` covers `extra`/`contexts`/breadcrumb and span data
 * (default `normalizeDepth: 3`; do NOT set it to 0), Datadog `sanitize()` clones
 * input at the public API boundary, Replay normalizes breadcrumbs before
 * `beforeAddRecordingEvent`, and the live-object root `sdkProcessingMetadata` is
 * detached from the walk upstream. No code in `frontend/web/src` constructs such a
 * descriptor. Frozen containers ARE handled — see `toWritableClone`. */
export function redactOwnDataProperty(
  record: Record<string, unknown>,
  key: string,
  redact: (child: unknown) => unknown,
): void {
  const descriptor = Object.getOwnPropertyDescriptor(record, key);
  if (!descriptor || descriptor.get !== undefined || descriptor.set !== undefined) {
    return;
  }
  const child = record[key];
  const next = redact(child);
  if (next === child) {
    return;
  }
  if (descriptor.writable === true) {
    record[key] = next;
  } else if (descriptor.configurable === true) {
    Object.defineProperty(record, key, {
      value: next,
      writable: false,
      enumerable: descriptor.enumerable,
      configurable: true,
    });
  }
}

function redactRecordKey(record: Record<string, unknown>, key: string, depth: number): void {
  redactOwnDataProperty(record, key, (child) => {
    const normalizedKey = normalizeObjectKey(key);
    const structuredError =
      normalizedKey === 'error' && child !== null && typeof child === 'object';
    return SENSITIVE_OBJECT_KEYS.has(normalizedKey) && !structuredError
      ? REDACTION_PLACEHOLDER
      : redactSensitiveStringsDeep(child, depth + 1);
  });
}
